// Per-frame body/face metadata that travels next to the packed video frame
// (SA B2, B8). DOM-free: shared by the browser (pipeline, stage, demo), the
// strip renderer and the Node render worker.
//
// Conventions (SA B2 "một quy ước gương"):
//  * every coordinate here is normalised to the SOURCE camera frame
//    (x: 0..1 of its width, y: 0..1 of its height) and is UN-MIRRORED, i.e.
//    exactly what the camera sensor delivered. Mirroring happens exactly once,
//    when the whole stage is shown (render/stage.ts) or saved (booth/strip.ts).
//  * z is MediaPipe Pose's relative depth (hips-midpoint origin, same scale as x,
//    smaller = closer to the camera).

/** MediaPipe Pose landmark indices (33 points). */
export const KP = {
  nose: 0,
  leftEye: 2,
  rightEye: 5,
  leftShoulder: 11,
  rightShoulder: 12,
  leftElbow: 13,
  rightElbow: 14,
  leftWrist: 15,
  rightWrist: 16,
  leftPinky: 17,
  rightPinky: 18,
  leftIndex: 19,
  rightIndex: 20,
  leftHip: 23,
  rightHip: 24,
  leftAnkle: 27,
  rightAnkle: 28,
} as const;
export const KP_COUNT = 33;
/** values per keypoint in `PoseFrame.kp`: x, y, z, visibility */
export const KP_STRIDE = 4;

export type Side = 'left' | 'right';
/** the anatomical arm of the subject (MediaPipe's "left" = subject's left) */
export const ARM_JOINTS: Record<Side, { shoulder: number; elbow: number; wrist: number; index: number; pinky: number }> = {
  left: { shoulder: KP.leftShoulder, elbow: KP.leftElbow, wrist: KP.leftWrist, index: KP.leftIndex, pinky: KP.leftPinky },
  right: { shoulder: KP.rightShoulder, elbow: KP.rightElbow, wrist: KP.rightWrist, index: KP.rightIndex, pinky: KP.rightPinky },
};

/** Eyes from the Face Landmarker (iris centres), source-normalised, un-mirrored. */
export interface FaceInfo {
  /** the eye with the smaller x in the source image */
  eyeL: [number, number];
  /** the eye with the larger x in the source image */
  eyeR: [number, number];
  /** inter-pupil distance in source pixels / source height */
  ipd: number;
  /** head roll in radians (0 = eyes level; positive = eyeR lower than eyeL in the image) */
  roll: number;
}

/** CIELAB mean / std of the person region (low-res), for Reinhard colour matching. */
export interface ColorStats {
  mean: [number, number, number];
  std: [number, number, number];
}

export interface PoseFrame {
  /** frame sequence number, also written as a barcode into the packed video frame (16 bit) */
  seq: number;
  /** sender's performance.now() when the camera frame was processed */
  t: number;
  /** source frame aspect ratio (width / height) */
  aspect: number;
  /** KP_COUNT × (x, y, z, visibility), or null when no body was found */
  kp: ArrayLike<number> | null;
  face: FaceInfo | null;
  color: ColorStats | null;
}

export const kpX = (f: PoseFrame, i: number) => f.kp![i * KP_STRIDE];
export const kpY = (f: PoseFrame, i: number) => f.kp![i * KP_STRIDE + 1];
export const kpZ = (f: PoseFrame, i: number) => f.kp![i * KP_STRIDE + 2];
export const kpV = (f: PoseFrame, i: number) => f.kp![i * KP_STRIDE + 3];

/** Builds FaceInfo from two eye centres (any order) of a frame with the given aspect. */
export function faceFromEyes(a: [number, number], b: [number, number], aspect: number): FaceInfo {
  const [eyeL, eyeR] = a[0] <= b[0] ? [a, b] : [b, a];
  const dx = (eyeR[0] - eyeL[0]) * aspect; // in units of source height
  const dy = eyeR[1] - eyeL[1];
  return { eyeL, eyeR, ipd: Math.hypot(dx, dy), roll: Math.atan2(dy, dx) };
}

// ------------------------------------------------------------------ wire format

/**
 * Compact pose message sent over the data channel once per processed frame.
 * Integers keep JSON small and make the receiver's maths deterministic (SA B5.6:
 * coordinates are on a 1/1024 grid before any comparison).
 */
export interface PoseFrameMsg {
  /** seq */
  s: number;
  /** sender time (ms) */
  t: number;
  /** aspect × 1000 */
  a: number;
  /** keypoints × 1024 (x, y, z, vis), omitted when no body */
  k?: number[];
  /** eyes × 4096: lx, ly, rx, ry */
  f?: [number, number, number, number];
  /** Lab mean (3) + std (3), × 10 */
  c?: [number, number, number, number, number, number];
}

const Q = 1024;
const QF = 4096;

export const quant = (v: number) => Math.round(v * Q) / Q;

export function encodePose(p: PoseFrame): PoseFrameMsg {
  const m: PoseFrameMsg = { s: p.seq & 0xffff, t: Math.round(p.t), a: Math.round(p.aspect * 1000) };
  if (p.kp) {
    const k = new Array<number>(KP_COUNT * KP_STRIDE);
    for (let i = 0; i < k.length; i++) k[i] = Math.round(p.kp[i] * Q);
    m.k = k;
  }
  if (p.face) m.f = [p.face.eyeL[0], p.face.eyeL[1], p.face.eyeR[0], p.face.eyeR[1]].map((v) => Math.round(v * QF)) as PoseFrameMsg['f'];
  if (p.color) m.c = [...p.color.mean, ...p.color.std].map((v) => Math.round(v * 10)) as PoseFrameMsg['c'];
  return m;
}

export function decodePose(m: PoseFrameMsg): PoseFrame {
  const aspect = m.a / 1000 || 4 / 3;
  const kp = Array.isArray(m.k) && m.k.length === KP_COUNT * KP_STRIDE ? Float32Array.from(m.k, (v) => v / Q) : null;
  const face = Array.isArray(m.f) && m.f.length === 4
    ? faceFromEyes([m.f[0] / QF, m.f[1] / QF], [m.f[2] / QF, m.f[3] / QF], aspect) : null;
  const color = Array.isArray(m.c) && m.c.length === 6
    ? { mean: [m.c[0] / 10, m.c[1] / 10, m.c[2] / 10] as [number, number, number], std: [m.c[3] / 10, m.c[4] / 10, m.c[5] / 10] as [number, number, number] }
    : null;
  return { seq: m.s & 0xffff, t: m.t, aspect, kp, face, color };
}

/** Quantises a frame in place-equivalent (the sender uses this so its own view matches what peers decode). */
export function quantizePose(p: PoseFrame): PoseFrame {
  return { ...decodePose(encodePose(p)), seq: p.seq, t: p.t };
}

// ------------------------------------------------------------------ frame barcode

/**
 * The packed video frame carries its own sequence number as a 1-row barcode in
 * a reserved strip under the [RGB | mask] area, so the receiver can join the
 * decoded frame with the pose message of exactly that frame.
 *   cells: [white, black, 16 data bits (MSB first), black, white]
 */
export const BAR_CELLS = 20;
/** strip height in pixels (one codec macroblock) */
export const BAR_H = 16;

export function barcodeCells(seq: number): boolean[] {
  const s = seq & 0xffff;
  const bits = Array.from({ length: 16 }, (_, i) => ((s >> (15 - i)) & 1) === 1);
  return [true, false, ...bits, false, true];
}

/** Decodes luminance samples (0..1 or 0..255) of the BAR_CELLS cells; null when the guards do not match. */
export function decodeBarcode(lum: ArrayLike<number>): number | null {
  if (lum.length < BAR_CELLS) return null;
  const hi = (lum[0] + lum[BAR_CELLS - 1]) / 2;
  const lo = (lum[1] + lum[BAR_CELLS - 2]) / 2;
  if (!(hi - lo > (hi > 2 ? 60 : 0.25))) return null; // no strip (old sender) or garbage
  const thr = (hi + lo) / 2;
  let s = 0;
  for (let i = 0; i < 16; i++) s = (s << 1) | (lum[2 + i] > thr ? 1 : 0);
  return s;
}

/** Signed distance between two 16-bit sequence numbers (handles wrap-around). */
export function seqDelta(a: number, b: number) {
  let d = (a - b) & 0xffff;
  if (d >= 0x8000) d -= 0x10000;
  return d;
}

/**
 * Small ring of recent pose frames keyed by seq. The receiver looks up the
 * pose of the exact frame it is drawing; when that one is missing (lost or late
 * message, old sender without barcode) it falls back to the latest.
 */
export class PoseRing {
  private m = new Map<number, PoseFrame>();
  latest: PoseFrame | null = null;
  /** how many lookups hit the exact frame (diagnostics) */
  hits = 0;
  misses = 0;

  constructor(private readonly cap = 90) {}

  push(p: PoseFrame) {
    this.m.set(p.seq & 0xffff, p);
    if (this.m.size > this.cap) this.m.delete(this.m.keys().next().value!);
    if (!this.latest || seqDelta(p.seq, this.latest.seq) > 0 || Math.abs(seqDelta(p.seq, this.latest.seq)) > 1000) this.latest = p;
  }

  get(seq: number | null): PoseFrame | null {
    if (seq != null) {
      const p = this.m.get(seq & 0xffff);
      if (p) { this.hits++; return p; }
    }
    this.misses++;
    return this.latest;
  }

  clear() {
    this.m.clear();
    this.latest = null;
  }
}
